import { FC, Fragment } from 'react'
import { Marker, InfoWindow } from '@react-google-maps/api'
import { Clusterer } from '@react-google-maps/marker-clusterer'
import { CemeteryTinyItem } from '@lifedot/components/CemeteryTinyItem'
import { makeMapItem } from './dependencies'
import { Divider } from '@lifedot/atoms/Divider'
import { css } from '@emotion/react'
import { useQuery } from 'relay-hooks/lib/useQuery'
import { graphql } from 'relay-runtime'
import { dependenciesQuery$data } from './__generated__/dependenciesQuery.graphql'
import { CemeteryMarkerQuery } from './__generated__/CemeteryMarkerQuery.graphql'

// google.maps.InfoWindowOptionsにminWidthの型がないため強制的に型をつける
const infoWindowOptions = {
  minWidth: 320
} as google.maps.InfoWindowOptions

interface CemeteryMarkerProps {
  position: {
    lat: number
    lng: number
  }
  clusterer: Clusterer
  items: dependenciesQuery$data['listMapItems']['items']
  showInfoWindow: boolean
  openInfoWindow: () => void
  closeInfoWindow: () => void
}

export const CemeteryMarker: FC<CemeteryMarkerProps> = ({
  position,
  clusterer,
  items,
  showInfoWindow,
  openInfoWindow,
  closeInfoWindow
}) => {
  return (
    <Marker
      clusterer={clusterer}
      position={position}
      clickable
      onLoad={(marker) => marker.addListener('click', openInfoWindow)}
    >
      {showInfoWindow && (
        <InfoWindow
          position={position}
          onCloseClick={closeInfoWindow}
          options={infoWindowOptions}
        >
          <>
            {items.map(({ cemeteryId }, index) => (
              <Fragment key={index}>
                {index > 0 && <Divider />}
                <CemeteryMapItem id={cemeteryId} />
              </Fragment>
            ))}
          </>
        </InfoWindow>
      )}
    </Marker>
  )
}

const styles = {
  item: css({
    padding: '4px 0'
  })
}

interface CemeteryMapItemProps {
  id: number
}

const CemeteryMapItem: FC<CemeteryMapItemProps> = ({ id }) => {
  const { data } = useQuery<CemeteryMarkerQuery>(mapItem, { id })
  if (!data?.item) return null

  return (
    <div id={`mapCemetery_${id}`}>
      <div css={styles.item}>
        <CemeteryTinyItem {...makeMapItem(data.item)} />
      </div>
    </div>
  )
}

export const mapItem = graphql`
  query CemeteryMarkerQuery($id: Int!) {
    item: getCemetery(id: $id) {
      cemeteryId: id
      name
      section_types {
        name
      }
      cemetery_types {
        name
        code
      }
      sects {
        name
      }
      review_summary {
        count
        total_score
      }
      main_image {
        alt
        path
      }
      best_price_by_category {
        normal {
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
        nokotsu {
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
        jumoku {
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
        eitai_kuyo {
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
      }
    }
  }
`
